import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@hybrid-iptv/ui";
import { nativeGuideAvailable, nativeGuideFetch, nativeGuideProbe, nativeGuideStatus, type GuideSourceStatus } from "../api/nativeGuide.js";
import { providerBody } from "../epg/api.js";
import { MAX_EXTRA_FEEDS, addFeed, feedLabel, isGuideAddress, moveFeed, removeFeed, shiftFeed, toggleFeed } from "../epg/guideFeeds.js";
import { clampShift } from "../epg/guideMapStore.js";
import { ShiftStepper } from "../epg/ShiftStepper.js";
import { useSettings } from "./settingsStore.js";

/** How often the statuses are read again while the list is open (a download moves them). */
const STATUS_POLL_MS = 5_000;
/** The guide's own refresh age (api/deviceApi.ts GUIDE_TTL_MS): an extra source refreshes on the same clock. */
const SOURCE_TTL_MS = 6 * 60 * 60 * 1000;

const CREDENTIAL_INPUT = { autoCapitalize: "none", autoCorrect: "off", spellCheck: false } as const;

function ago(ms: number): string {
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} h ago` : `${Math.round(hours / 24)} days ago`;
}

function statusLine(status: GuideSourceStatus | null | undefined, enabled: boolean): string {
  if (!enabled) return "Switched off";
  if (!status) return "…";
  if (status.downloading && !status.held) return "Downloading…";
  if (!status.held) return "Not downloaded yet";
  const parts = [
    `Updated ${ago(status.fetchedAt ?? Date.now())}`,
    `${(status.feedChannels ?? 0).toLocaleString()} channels`,
    `${(status.programmes ?? 0).toLocaleString()} programmes`,
    `${Math.round(status.diskBytes / 1_048_576)} MB`,
  ];
  return (status.downloading ? "Updating… · " : "") + parts.join(" · ");
}

const PROBE_REFUSALS: Record<string, string> = {
  unreachable: "That address could not be reached.",
  not_guide: "That address is not a TV guide (XMLTV).",
};

/**
 * Settings → Playlists → Guide sources (Phase 3 step 3, docs/build-guide-playlists-epg.md §5):
 * the playlist's own guide first, then up to MAX_EXTRA_FEEDS more, in order. A channel takes its
 * programmes from the first that has it. Changes save at once, like the rest of Settings, and
 * the guide asks again. The TV app's only: the website keeps the one guide.
 */
export function GuideSources({ providerId }: { providerId: string }) {
  const provider = useSettings((s) => s.providers.find((p) => p.id === providerId));
  const updateProvider = useSettings((s) => s.updateProvider);
  const queryClient = useQueryClient();
  const feeds = provider?.guideFeeds ?? [];
  const [statuses, setStatuses] = useState<Record<string, GuideSourceStatus | null>>({});
  const [address, setAddress] = useState("");
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const urls = feeds.map((feed) => feed.url).join("\n");
  useEffect(() => {
    if (!provider) return;
    let stopped = false;
    const read = async () => {
      const { deviceOwnGuideUrl } = await import("../api/deviceApi.js");
      const own = await deviceOwnGuideUrl(providerBody(provider)).catch(() => null);
      const next: Record<string, GuideSourceStatus | null> = {};
      if (own) next[""] = await nativeGuideStatus(own).catch(() => null);
      for (const feed of provider.guideFeeds ?? []) next[feed.id] = await nativeGuideStatus(feed.url).catch(() => null);
      if (!stopped) setStatuses(next);
    };
    void read();
    const timer = window.setInterval(() => void read(), STATUS_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
    // The provider's sources, not its object: a validation stamp must not restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, urls]);

  if (!provider || !nativeGuideAvailable()) return null;

  const save = (next: typeof feeds) => {
    updateProvider(providerId, { guideFeeds: next });
    void queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === "epg" });
  };

  const add = async () => {
    const url = address.trim();
    if (!isGuideAddress(url)) return setMessage({ ok: false, text: "Enter a guide address starting with http:// or https://." });
    if (feeds.some((feed) => feed.url === url)) return setMessage({ ok: false, text: "That source is already in the list." });
    if (feeds.length >= MAX_EXTRA_FEEDS) return setMessage({ ok: false, text: `Up to ${MAX_EXTRA_FEEDS} extra sources.` });
    setChecking(true);
    setMessage(null);
    try {
      const probe = await nativeGuideProbe(url);
      if (!probe.ok) {
        const text = probe.reason === "http" ? `That address answered with an error (${probe.status}).` : PROBE_REFUSALS[probe.reason ?? "unreachable"]!;
        setMessage({ ok: false, text });
        return;
      }
      save(addFeed(feeds, url, `g${Date.now().toString(36)}`));
      setAddress("");
      setMessage({ ok: true, text: "Added. It downloads in the background; its channels fill in when it is done." });
      void nativeGuideFetch(url, SOURCE_TTL_MS).catch(() => undefined);
    } catch {
      setMessage({ ok: false, text: PROBE_REFUSALS.unreachable! });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="settings-section guide-sources">
      <h2 className="settings-card-title">Guide sources</h2>
      <p className="settings-card-desc">
        Extra guides fill the channels this playlist's own guide has no programmes for. A channel uses the first
        source that has it. Time shift moves a source's programmes earlier or later, for a guide that is off by an
        hour; one channel's own shift is under Choose guide, in the guide's hold-OK menu.
      </p>
      <div className="guide-source">
        <span className="guide-source__name">This playlist's guide</span>
        <span className="guide-source__status">{statusLine(statuses[""], true)}</span>
        <ShiftStepper
          minutes={provider.guideShiftMinutes ?? 0}
          onChange={(minutes) => updateProvider(providerId, { guideShiftMinutes: clampShift(minutes) })}
        />
      </div>
      {feeds.map((feed, index) => (
        <div key={feed.id} className="guide-source">
          <span className="guide-source__name">
            {index + 2}. {feedLabel(feed.url)}
          </span>
          <span className="guide-source__status">{statusLine(statuses[feed.id], feed.enabled)}</span>
          <ShiftStepper minutes={feed.shiftMinutes ?? 0} onChange={(minutes) => save(shiftFeed(feeds, feed.id, minutes))} />
          <div className="guide-source__actions">
            <Button variant="ghost" disabled={index === 0} onClick={() => save(moveFeed(feeds, feed.id, -1))}>
              Move up
            </Button>
            <Button variant="ghost" disabled={index === feeds.length - 1} onClick={() => save(moveFeed(feeds, feed.id, 1))}>
              Move down
            </Button>
            <Button variant="ghost" onClick={() => save(toggleFeed(feeds, feed.id))}>
              {feed.enabled ? "Switch off" : "Switch on"}
            </Button>
            <Button variant="ghost" onClick={() => save(removeFeed(feeds, feed.id))}>
              Remove
            </Button>
          </div>
        </div>
      ))}
      {feeds.length < MAX_EXTRA_FEEDS && (
        <label className="settings-field">
          <span className="settings-label">Add a guide source</span>
          <input
            type="url"
            className="settings-input"
            placeholder="https://example.com/guide.xml.gz"
            {...CREDENTIAL_INPUT}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </label>
      )}
      {feeds.length < MAX_EXTRA_FEEDS && (
        <div className="settings-actions">
          <Button variant="default" disabled={checking || !address.trim()} onClick={() => void add()}>
            {checking ? "Checking…" : "Add source"}
          </Button>
        </div>
      )}
      {message && <div className={`settings-result ${message.ok ? "settings-result--ok" : "settings-result--fail"}`}>{message.text}</div>}
    </div>
  );
}
